import { Pipe, PipeTransform } from '@angular/core';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { selectConfigSitesFilters } from '../../features/config/config.selectors';
import { Site } from '../../models/site';
import {
  SiteFilterDetectionKeys,
  SiteFilterKeys,
  SiteFilterVolumeKeys
} from '../../enums/site-filter';

@Pipe({
  name: 'filters',
  standalone: false
})
export class FiltersPipe implements PipeTransform {

  private filters: any | undefined;

  constructor(private store: Store) {
    this.initHandleSiteFilters();
  }

  private initHandleSiteFilters(): void {
    this.store.select(selectConfigSitesFilters)
      .pipe(takeUntilDestroyed())
      .subscribe((filters: any | undefined) => {
        this.filters = filters;
      });
  }

  transform(items: Site[] | null | undefined): Site[] {
    if (!items) {
      return [];
    }

    if (!this.filters) {
      return items;
    }

    let filteredItems = this.sectorFilter(items);
    filteredItems = this.emergencyFilter(filteredItems);
    filteredItems = this.detectionFilter(filteredItems);
    filteredItems = this.volumeFilter(filteredItems);

    return filteredItems;
  }

  private sectorFilter(items: Site[]): Site[] {
    const sectors = this.filters?.[SiteFilterKeys.SECTORS];

    if (!sectors?.length) {
      return items;
    }

    return items.filter(item => sectors.includes(item.sector));
  }

  private emergencyFilter(items: Site[]): Site[] {
    const emergencies = this.filters?.[SiteFilterKeys.EMERGENCIES];

    if (!emergencies?.length) {
      return items;
    }

    return items.filter(item => emergencies.includes(String(item.emergencyLabel)));
  }

  private detectionFilter(items: Site[]): Site[] {
    const detectionFilters = this.filters?.[SiteFilterKeys.DETECTIONS];

    if (!detectionFilters) {
      return items;
    }

    return items.filter(item => {

      if (!item.detectionSummary && !item.volumeSummary) {
        return false;
      }

      // Site sans données de détection mais avec données de volume
      if (!item.detectionSummary) {
        return true;
      }

      const totalSettings =
        detectionFilters[SiteFilterDetectionKeys.TOTAL];

      const emergencySettings =
        detectionFilters[SiteFilterDetectionKeys.HIGH_EMERGENCY_LEVEL];

      const densityPeakSettings =
        detectionFilters[SiteFilterDetectionKeys.DENSITY_PEAK];

      return (
        totalSettings.start <= item.detectionSummary.detectionCount &&
        item.detectionSummary.detectionCount <= totalSettings.end &&
        emergencySettings.start <= item.detectionSummary.emergencyCount &&
        item.detectionSummary.emergencyCount <= emergencySettings.end &&
        densityPeakSettings.start <= item.detectionSummary.densityPeak &&
        item.detectionSummary.densityPeak <= densityPeakSettings.end
      );
    });
  }

  private volumeFilter(items: Site[]): Site[] {
    const volumeFilters = this.filters?.[SiteFilterKeys.VOLUMES];

    if (!volumeFilters) {
      return items;
    }

    return items.filter(item => {

      if (!item.detectionSummary && !item.volumeSummary) {
        return false;
      }

      // Site sans données de volume mais avec données de détection
      if (!item.volumeSummary) {
        return true;
      }

      const positiveSettings =
        volumeFilters[SiteFilterVolumeKeys.POSITIVE_VOLUME_MOVED];

      const negativeSettings =
        volumeFilters[SiteFilterVolumeKeys.NEGATIVE_VOLUME_MOVED];

      const emergencySettings =
        volumeFilters[SiteFilterVolumeKeys.HIGH_EMERGENCY_LEVEL];

      const densityPeakSettings =
        volumeFilters[SiteFilterVolumeKeys.DENSITY_PEAK];

      return (
        positiveSettings.start <= item.volumeSummary.positiveVolumeMoved &&
        item.volumeSummary.positiveVolumeMoved <= positiveSettings.end &&
        negativeSettings.start <= item.volumeSummary.negativeVolumeMoved &&
        item.volumeSummary.negativeVolumeMoved <= negativeSettings.end &&
        emergencySettings.start <= item.volumeSummary.emergencyCount &&
        item.volumeSummary.emergencyCount <= emergencySettings.end &&
        densityPeakSettings.start <= item.volumeSummary.densityPeak &&
        item.volumeSummary.densityPeak <= densityPeakSettings.end
      );
    });
  }
}
